import {endOfDay, startOfDay} from 'date-fns';
import {zonedTimeToUtc} from 'date-fns-tz';
import {SearchCriteria} from '..';
import {ActiveFilter} from '../components';

type FilterOpts = {
  /**
   * If set to true, date will be parsed to UTC before it is encoded
   * Eg.:
   *  - Input date:   "2025-01-01T00:00:00-03:00"
   *  - Output date:  "2025-01-01T00:00:00Z"
   */
  useUtcDate?: boolean;

  /**
   * If set to true, the time of the start and end dates will be set
   * Eg.:
   * - Start date:  "2025-01-01T00:00:00"
   * - End date:    "2025-01-15T23:59:59"
   */
  enforceDateRangeTime?: boolean;
};

export const toDateRangeQuery = (
  range: [Date, Date],
  column: string,
  operatorId: string,
  filterOpts?: FilterOpts
) => {
  let [fromDate, toDate] = range as [Date, Date];

  if (filterOpts?.enforceDateRangeTime) {
    fromDate = startOfDay(fromDate);
    toDate = endOfDay(toDate);
  }

  if (filterOpts?.useUtcDate) {
    fromDate = zonedTimeToUtc(fromDate, 'UTC');
    toDate = zonedTimeToUtc(toDate, 'UTC');
  }

  const fromDateParam = encodeURIComponent(fromDate.toISOString());
  const toDateParam = encodeURIComponent(toDate.toISOString());
  return `${column}${operatorId}=${fromDateParam},${toDateParam}`;
};

export const toQuery = (
  searchCriteria: SearchCriteria | null,
  filters?: ActiveFilter[],
  filterOpts?: FilterOpts
) => {
  let params = [];

  if (searchCriteria) {
    params.push(
      `page=${searchCriteria.offset}`,
      `count=${searchCriteria.limit}`,
      `order_by=${searchCriteria.order === 'desc' ? '-' : ''}${searchCriteria.orderBy}`
    );
    if (searchCriteria.index !== undefined) {
      params.push(`index=${searchCriteria.index}`);
    }
  }

  if (filters) {
    filters.forEach(filter => {
      if (filter.option.type === 'dateRange' || filter.option.type === 'date') {
        const query = toDateRangeQuery(
          filter.value as [Date, Date],
          filter.option.column,
          filter.operator.id,
          filterOpts
        );
        params.push(query);

        return;
      }
      if (['dateOnlyRange', 'dateOnly', 'monthRange', 'month'].includes(filter.option.type)) {
        const [start, end] = filter.value as [Date, Date];

        const startDate = `${start.getFullYear()}-${(start.getMonth() + 1)
          .toString()
          .padStart(2, '0')}-${start.getDate().toString().padStart(2, '0')}`; // Date provides month index; not month number
        const endDate = `${end.getFullYear()}-${(end.getMonth() + 1)
          .toString()
          .padStart(2, '0')}-${end.getDate().toString().padStart(2, '0')}`; // Date provides month index; not month number

        params.push(
          `${filter.option.column}${filter.operator.id}=${startDate}${
            ['dateOnlyRange', 'monthRange', 'month'].includes(filter.option.type)
              ? `,${endDate}`
              : ''
          }`
        );
        return;
      }
      params.push(
        `${filter.option.id}${filter.operator.id}=${encodeURIComponent(filter.value as string)}`
      );
    });
  }
  return [...params].join('&');
};

declare global {
  interface Array<T> {
    sortAndMap<U>(
      callbackfn: (value: T, index: number, array: T[]) => U,
      getValueFn?: (value: T) => string,
      thisArg?: any
    ): U[] | undefined;
  }
}

if (!Array.prototype.sortAndMap) {
  Array.prototype.sortAndMap = function <U, T>(
    callbackfn: (value: T, index: number, array: T[]) => U,
    getValueFn?: (value: T) => string,
    thisArg?: any
  ): U[] {
    return getSortedOptions(this, getValueFn).map(callbackfn);
  };
}

export function getSortedKeys<T>(o: T[], getValue: (value: T) => string): string[] {
  const options = getSortedOptions(o, getValue);
  return options ? Object.keys(options) : [];
}

export function getSortedOptions<T>(o: T[], getValue?: (value: T) => string): T[] {
  const value = (a: T) => (getValue ? getValue(a) : a) as string;

  const compareFn = (a: T, b: T) => value(a).toString().localeCompare(value(b).toString());
  return o?.sort ? o?.sort(compareFn) : o;
}
